// ─── Space Route — PERMANENT back-compat alias (ADR 0012 D5) ───
// `/app/spaces/$spaceId` never had a detail page before this feature (the old
// breadcrumb linked here but no route file existed — a dead link, see the
// ADR's Context section). It now exists as a loader-only alias to the
// canonical friendly space URL (`/app/s/$spaceRef`), resolved the same way as
// the list alias: by the raw id's short suffix (no slug available in this URL
// shape), scope-checked, redirecting with search params preserved.
//
// IMPORTANT — this file is also the file-based-routing PARENT of
// `routes/app/spaces/$spaceId/lists/$listId.tsx` (the list alias lives one
// directory below). TanStack Router runs every matched ancestor's loader for
// a given navigation, so a deep link to
// `/app/spaces/{spaceId}/lists/{listId}` matches BOTH this route (as an
// ancestor) and the list alias (as the leaf). This loader must only redirect
// when IT is the actual navigation target — i.e. the current location is
// exactly `/app/spaces/{spaceId}` with nothing after it — otherwise it must
// no-op and let the (matched-as-leaf) list alias below run its own redirect.
// Left unguarded, this loader would hijack every nested list deep-link into a
// bare space redirect, silently dropping the requested list. When this route
// is only an ancestor, it renders nothing itself (no `component` is
// registered, so TanStack Router's default falls back to `<Outlet />`,
// letting the matched child render/redirect).

import { createFileRoute, redirect } from '@tanstack/react-router';
import { shortId } from '@a8-planner/shared/utils/entity-url';
import { resolveEntity } from '../../../lib/entity-resolve';

export const Route = createFileRoute('/app/spaces/$spaceId')({
  loader: async ({ params, location }): Promise<void> => {
    const ownPath = `/app/spaces/${params.spaceId}`;
    if (location.pathname !== ownPath) {
      // We're only an ancestor of a deeper match (e.g. the nested list alias)
      // — that route owns its own resolve + redirect. Nothing to do here.
      return;
    }

    const resolved = await resolveEntity('space', shortId(params.spaceId));
    throw redirect({
      to: resolved.canonicalPath as '/app',
      search: (prev) => prev,
      replace: true,
    });
  },
});
